/**
 * Shared header controls — search, theme, language and the mobile overflow
 * menu. Extracted from SystemShell so the desktop header and MobileHeader
 * render the exact same items (one definition of "what a language switch
 * does", one definition of the theme preference).
 *
 * Every header DropdownMenu here is `modal={false}`. A modal Radix menu sets
 * `pointer-events: none` on <body> and swallows the first outside click, which
 * made the next header button need two clicks. Non-modal menus still close on
 * outside pointer-down/Escape/focus-out, but let that same click through.
 */
import { useCallback, useEffect, useState } from "react";
import type { TFunction } from "i18next";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import Check from "lucide-react/dist/esm/icons/check.js";
import EllipsisVertical from "lucide-react/dist/esm/icons/ellipsis-vertical.js";
import Languages from "lucide-react/dist/esm/icons/languages.js";
import Monitor from "lucide-react/dist/esm/icons/monitor.js";
import Moon from "lucide-react/dist/esm/icons/moon.js";
import Search from "lucide-react/dist/esm/icons/search.js";
import Sun from "lucide-react/dist/esm/icons/sun.js";

import { Button } from "@shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@shared/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { cn } from "@shared/lib/utils";

/** Same value as `GLOBAL_COMMAND_MENU_EVENT` in components/command-menu. */
const OPEN_COMMAND_MENU_EVENT = "app:open-command-menu";

export const SUPPORTED_LANGUAGES = ["ar", "de", "en", "es", "fr"] as const;
export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGES)[number];
const DEFAULT_LANGUAGE: SupportedLanguageCode = "en";

/**
 * Language names in their own script. Deliberately NOT translated: a user who
 * landed in the wrong language must still be able to find their own.
 */
export const LANGUAGE_OPTIONS: ReadonlyArray<{
  code: SupportedLanguageCode;
  nativeName: string;
  dir: "ltr" | "rtl";
}> = [
  { code: "en", nativeName: "English", dir: "ltr" },
  { code: "ar", nativeName: "العربية", dir: "rtl" },
  { code: "de", nativeName: "Deutsch", dir: "ltr" },
  { code: "es", nativeName: "Español", dir: "ltr" },
  { code: "fr", nativeName: "Français", dir: "ltr" },
];

export function translateWithFallback(
  t: TFunction<any>,
  key: string,
  defaultValue: string,
  options: Record<string, unknown> = {},
): string {
  if (!key) return defaultValue;
  const translated: unknown = t(key, { defaultValue, ...options });
  return typeof translated === "string" && translated !== key
    ? translated
    : defaultValue;
}

export function isSupportedLanguage(segment: string | undefined): boolean {
  return Boolean(
    segment && (SUPPORTED_LANGUAGES as readonly string[]).includes(segment),
  );
}

export function stripLanguagePrefix(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  if (isSupportedLanguage(segments[0])) {
    const stripped = `/${segments.slice(1).join("/")}`;
    return stripped === "/" ? "/" : stripped.replace(/\/$/, "");
  }
  return pathname === "" ? "/" : pathname;
}

/**
 * Path for `pathname` in language `lang`.
 *
 * Convention (matches `localizedPath` / `activeLanguagePrefix`): English is the
 * unprefixed default, every other language is `/<lang>/…`. So:
 *   /de/symptoms -> en  => /symptoms
 *   /symptoms    -> ar  => /ar/symptoms
 *   /de/symptoms -> ar  => /ar/symptoms
 * An unprefixed root ("/") has no locale segment to swap and is left alone;
 * a prefixed root ("/de") becomes "/ar" (or "/" for English).
 */
export function replacePathLanguage(pathname: string, lang: string): string {
  const firstSegment = pathname.split("/").filter(Boolean)[0];
  const hadPrefix = isSupportedLanguage(firstSegment);
  const stripped = stripLanguagePrefix(pathname);
  const prefix = lang === DEFAULT_LANGUAGE ? "" : `/${lang}`;
  if (stripped === "/") return hadPrefix ? prefix || "/" : "/";
  return `${prefix}${stripped}`;
}

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const THEME_STORAGE_KEY = "theme";
const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";

function isThemePreference(value: unknown): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}

/** localStorage can throw (private mode, blocked site data): never let it. */
function readStoredTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(DARK_SCHEME_QUERY).matches
  );
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference !== "system") return preference;
  return systemPrefersDark() ? "dark" : "light";
}

export function applyTheme(preference: ThemePreference): void {
  const resolved = resolveTheme(preference);
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(resolved);
  root.style.colorScheme = resolved;
  try {
    if (preference === "system") {
      localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
  } catch {
    // Persistence is a convenience; the theme still applies for this session.
  }
}

/**
 * The stored preference (`light` | `dark` | `system`), the theme it currently
 * resolves to, and a setter. While the preference is `system` the hook follows
 * OS-level `prefers-color-scheme` changes live (it used to read them once).
 * Returned as `[preference, setPreference, resolved]`; callers that only need
 * the first two keep destructuring two.
 */
export function useThemePreference(): [
  ThemePreference,
  (next: ThemePreference) => void,
  ResolvedTheme,
] {
  const [preference, setPreference] = useState<ThemePreference>(() =>
    typeof window === "undefined" ? "system" : readStoredTheme(),
  );
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);
  const resolved: ResolvedTheme =
    preference === "system" ? (systemDark ? "dark" : "light") : preference;

  useEffect(() => {
    applyTheme(preference);
  }, [preference, resolved]);

  useEffect(() => {
    if (preference !== "system" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mql = window.matchMedia(DARK_SCHEME_QUERY);
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    setSystemDark(mql.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [preference]);

  // Keep several tabs / several mounted menus in agreement.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== THEME_STORAGE_KEY && event.key !== null) return;
      setPreference(readStoredTheme());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return [preference, setPreference, resolved];
}

/**
 * Switch language and keep the user exactly where they are: same path (with
 * the locale prefix swapped), same query string, same hash.
 */
export function useLanguageSwitch() {
  const { i18n } = useTranslation("common");
  const location = useLocation() as {
    pathname: string;
    search?: unknown;
    hash?: string;
  };
  const navigate = useNavigate();
  const { pathname, search, hash } = location;

  return useCallback(
    async (languageCode: string) => {
      await i18n.changeLanguage(languageCode);
      const nextPath = replacePathLanguage(pathname, languageCode);
      if (nextPath === pathname) return;
      const cleanHash = typeof hash === "string" ? hash.replace(/^#/, "") : "";
      void navigate({
        to: nextPath,
        search: search ?? {},
        hash: cleanHash || undefined,
      } as never);
    },
    [hash, i18n, navigate, pathname, search],
  );
}

export function openCommandMenu(): void {
  window.dispatchEvent(new CustomEvent(OPEN_COMMAND_MENU_EVENT));
}

function useIsMac(): boolean {
  return (
    typeof navigator !== "undefined" &&
    (/mac/i.test(navigator.platform) || /mac/i.test(navigator.userAgent))
  );
}

// ---------------------------------------------------------------------------
// Menu items (shared between desktop dropdowns and the mobile overflow menu)
// ---------------------------------------------------------------------------

export function ThemeMenuItems({
  preference,
  onChange,
}: {
  preference: ThemePreference;
  onChange: (next: ThemePreference) => void;
}) {
  const { t } = useTranslation("common");
  return (
    <>
      {(["light", "dark", "system"] as const).map((theme) => (
        <DropdownMenuItem
          key={theme}
          role="menuitemradio"
          aria-checked={preference === theme}
          onSelect={() => onChange(theme)}
        >
          <span className="flex-1">
            {translateWithFallback(t, `theme.${theme}`, theme)}
          </span>
          {preference === theme && (
            <Check className="ms-2 h-4 w-4" aria-hidden="true" />
          )}
        </DropdownMenuItem>
      ))}
    </>
  );
}

export function LanguageMenuItems() {
  const { i18n } = useTranslation("common");
  const switchLanguage = useLanguageSwitch();
  const currentLanguage = i18n.language?.split("-")[0] ?? DEFAULT_LANGUAGE;

  return (
    <>
      {LANGUAGE_OPTIONS.map((language) => (
        <DropdownMenuItem
          key={language.code}
          lang={language.code}
          dir={language.dir === "rtl" ? "rtl" : undefined}
          role="menuitemradio"
          aria-checked={currentLanguage === language.code}
          onSelect={() => void switchLanguage(language.code)}
        >
          <span className="flex-1">{language.nativeName}</span>
          {currentLanguage === language.code && (
            <Check className="ms-2 h-4 w-4" aria-hidden="true" />
          )}
        </DropdownMenuItem>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Desktop header buttons
// ---------------------------------------------------------------------------

export function ThemeMenu() {
  const { t } = useTranslation("common");
  const [preference, setPreference, resolved] = useThemePreference();
  const label = translateWithFallback(t, "theme.title", "Theme");
  const modeLabel = translateWithFallback(t, `theme.${preference}`, preference);
  const tooltip = translateWithFallback(
    t,
    "theme.current",
    `Theme: ${modeLabel}`,
    { mode: modeLabel },
  );
  const Icon = resolved === "dark" ? Moon : Sun;

  return (
    <Tooltip>
      <DropdownMenu modal={false}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={label}
              data-theme-preference={preference}
            >
              {/* Sun/Moon follow the theme actually on screen; the Monitor
                  badge marks "System" so the three states stay distinguishable. */}
              <span className="relative inline-flex">
                <Icon className="h-4 w-4" aria-hidden="true" />
                {preference === "system" && (
                  <Monitor
                    data-testid="theme-system-badge"
                    className="absolute -bottom-1 -end-1 h-2.5 w-2.5 rounded-sm bg-background"
                    aria-hidden="true"
                  />
                )}
              </span>
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <DropdownMenuContent align="end">
          <ThemeMenuItems preference={preference} onChange={setPreference} />
        </DropdownMenuContent>
      </DropdownMenu>
      <TooltipContent side="bottom">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

export function LanguageMenu() {
  const { t } = useTranslation("common");
  const label = translateWithFallback(
    t,
    "language.changeLanguage",
    "Change language",
  );

  return (
    <Tooltip>
      <DropdownMenu modal={false}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={label}>
              <Languages className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <DropdownMenuContent align="end">
          <LanguageMenuItems />
        </DropdownMenuContent>
      </DropdownMenu>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

export function SearchTrigger() {
  const { t } = useTranslation("common");
  const shortcutLabel = useIsMac() ? "⌘K" : "Ctrl K";
  const label = translateWithFallback(t, "commandMenu.openHint", "Search");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={label}
          onClick={openCommandMenu}
        >
          <Search className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {label}
        <kbd className="ms-2 rounded border bg-muted px-1 py-0.5 text-[10px] text-muted-foreground">
          {shortcutLabel}
        </kbd>
      </TooltipContent>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Mobile overflow menu
// ---------------------------------------------------------------------------

/**
 * Search, theme and language for viewports where the desktop header buttons do
 * not fit. One trigger, one menu, all three controls reachable by touch
 * (items are 44px tall on coarse pointers via the DropdownMenuItem defaults).
 */
export function HeaderOverflowMenu({ className }: { className?: string }) {
  const { t } = useTranslation("common");
  const { t: tMobile } = useTranslation("mobile");
  const [preference, setPreference] = useThemePreference();
  const searchLabel = translateWithFallback(t, "commandMenu.openHint", "Search");

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={translateWithFallback(
            tMobile,
            "header.moreOptions",
            "More options",
          )}
          data-testid="header-overflow-trigger"
          className={cn(
            "inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className,
          )}
        >
          <EllipsisVertical className="h-5 w-5" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onSelect={openCommandMenu}>
          <Search className="me-2 h-4 w-4" aria-hidden="true" />
          <span className="flex-1">{searchLabel}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>
          {translateWithFallback(t, "theme.title", "Theme")}
        </DropdownMenuLabel>
        <ThemeMenuItems preference={preference} onChange={setPreference} />
        <DropdownMenuSeparator />
        <DropdownMenuLabel>
          {translateWithFallback(t, "language.select", "Select Language")}
        </DropdownMenuLabel>
        <LanguageMenuItems />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
